import { Tab, Tabs } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';

import PERMISSIONS from '@/constants/auth.constants';
import {
  orgProductsAtom,
  selectedOrganizationAtom
} from '@/pages/Organizations';
import {
  checkPermissions,
  checkProducts,
  checkUserPreferences
} from '@/services/permissions';
import userAtom from '@/store/user.store';
import useStyles from '../Dashboard.styles';
import { DASHBOARD_CONSTANTS } from '../dashboard.constants';
import type { DashboardTabType } from './DashboardTabs.types';

const DashboardTabs = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const user = useAtomValue(userAtom);
  const selectedOrganization = useAtomValue(selectedOrganizationAtom);
  const orgProducts = useAtomValue(orgProductsAtom);
  const { pathname } = useLocation();
  const { label } = selectedOrganization;

  const hasMCSCProducts = useMemo(() => {
    return orgProducts.some((product) => product.product_type === 'MCSC');
  }, [orgProducts]);

  // Get permitted tabs based on user permissions and organization products
  const permittedTabs = useMemo(() => {
    return DASHBOARD_CONSTANTS.filter((tabItem: DashboardTabType) => {
      return (
        checkPermissions(
          tabItem.requiredPermissions,
          user,
          selectedOrganization
        ) &&
        checkProducts(
          orgProducts,
          tabItem.requiredProducts?.products,
          tabItem.requiredProducts?.logic
        ) &&
        (hasMCSCProducts
          ? true
          : checkUserPreferences(user, tabItem.requiredPreferences)) // If user has no MCSC products, check preferences to show splash page. If it has MCSC products, always show the tab
      );
    }).map((tabItem) => tabItem);
  }, [user, selectedOrganization, orgProducts]);

  // Handle edge navigation cases
  useEffect(() => {
    const allowedTabs = permittedTabs.map((tab) => tab.value);
    const currentPathTab = DASHBOARD_CONSTANTS.find(
      (tab) => pathname === tab.path
    );

    // Case: user is not internal or has no ticket read (base dashboard permission) for the selected organization, navigate to announcements
    if (
      label !== 'all' &&
      !user?.internal &&
      !user?.permissions[label]?.includes(PERMISSIONS.TICKETS_READ)
    ) {
      // If user doesn't have permission, redirect to announcements
      navigate('/announcements');
      return;
    }
    // Case 2: URL matches a tab
    if (currentPathTab) {
      // Case 2a: If URL tab isn't allowed, redirect to dashboard
      if (
        !allowedTabs.includes(currentPathTab?.value) &&
        pathname !== '/dashboard'
      ) {
        navigate('/dashboard');
      }
      return;
    }
    // Case 3: URL doesn't match any tab and we're not on dashboard summary
    navigate('/dashboard');
    // Otherwise: URL already matches the current tab or is the default dashboard, do nothing
  }, [pathname, navigate, permittedTabs, user, label]);

  // Only compute selected tab if needed
  const activeTab = useMemo(() => {
    return permittedTabs.find((tab) => tab.path === pathname)?.value ?? 0;
  }, [pathname, permittedTabs]);

  return (
    <Tabs
      allowScrollButtonsMobile
      aria-label="dashboard tabs"
      scrollButtons="auto"
      sx={{ pl: '1.5rem' }}
      value={activeTab}
      variant="scrollable"
    >
      {permittedTabs.map((tabItem) => (
        <Tab
          disableRipple
          key={tabItem.label}
          component={NavLink}
          to={tabItem.path}
          label={tabItem.label}
          value={tabItem.value}
          className={classes.tab}
        />
      ))}
    </Tabs>
  );
};

DashboardTabs.displayName = 'DashboardTabs';

export default DashboardTabs;
